import { ReactNode, useEffect, useMemo } from "react";
import { Button } from "@progress/kendo-react-buttons";
import style from './PaginatedList.module.scss'
import PaginationButtons, { ChangePageInput } from "./PaginatedList.PaginationButton";
import ItemsPerPageDropDown from "./PaginatedList.ItemsPerPageDropDown";
import { usePagination } from "state/pagination";
import _ from "lodash";
import { Icon } from "components/Icon/Icon";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";

interface PaginatedListProps<Type> {
  items:Type[],
  componentKey: string,
  children: (item: Type) => ReactNode,
  resetKey?: string
}

/**
 * A generic paginated list component that renders a list of items with pagination controls.
 *
 * @template Type The type of items being displayed in the list.
 *
 * @param {Object} props The props for the PaginatedList component.
 * @param {string} props.componentKey The key to differ the states
 * @param {Type[]} props.items The array of items to paginate.
 * @param {(item: Type) => ReactNode} props.children A render function for displaying each item.
 *
 * @returns {JSX.Element} The rendered paginated list component.
 *
 * @example
 * <PaginatedList items={[...Array(50).keys()]} componentKey='Key';;;;;;;;;>
 *   {item => <div key={item}>{item}</div>}
 * </PaginatedList>
 */
function PaginatedList<Type>({ items, children, componentKey, resetKey } : PaginatedListProps<Type>) {
  const paginationControl = usePagination<Type>(items,componentKey);

  const totalPages = useMemo(() => Math.ceil(items.length / paginationControl.itemsPerPage), [items, paginationControl.itemsPerPage]);
  
  useEffect(() => {
    if (paginationControl.currentPage > totalPages && totalPages > 0) {
      paginationControl.SetPage(totalPages);
    }
    if (paginationControl.currentPage < 1 && totalPages > 0) {
      paginationControl.SetPage(1);
    }
  }, [items, paginationControl.itemsPerPage, totalPages, paginationControl]);

  // when the accountId changes, reset to the first page
  useEffect(() => {
    paginationControl.SetPage(1);
  }, [resetKey]);

  const getPageNumbers = useMemo(() => {
    let pages: (number | string)[] = [];

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
  
    if (paginationControl.currentPage <= 4) {
      pages = [1, 2, 3, 4, 5, '...', totalPages];
    } else if (paginationControl.currentPage >= totalPages - 2) {
      pages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    } else {
      pages = [1, '...', paginationControl.currentPage - 1, paginationControl.currentPage, paginationControl.currentPage + 1, '...', totalPages];
    }
  
    return pages;
  }, [totalPages, paginationControl.currentPage]);
 
  const ButtonIndicator = ({ operation }: { operation: 'Previous' | 'Next' }) => {
    return ( 
      <Button
        onClick={() => operation === 'Previous' ? 
          paginationControl.SetPreviousPage()
          : paginationControl.SetNextPage()
        }
        disabled={
          (operation === 'Previous' && paginationControl.currentPage === 1)
          || (operation === 'Next' && paginationControl.currentPage === totalPages)
        } 
        rounded={'medium'}
      >
        {operation}
      </Button>
    )
  }
  if(_.isEmpty(items))
    return <></>

  return (
      <>
        <div>
          {
            items
              .slice(paginationControl.startIndex,paginationControl.endIndex)
              .map(item => (children(item)))
          }
        </div>
        <div className={style.pagination}>
          <div className={style.block}>
            <div>
              <ButtonIndicator operation = {'Previous'}/>
            </div>
            <div>
              <PaginationButtons
                currentPage={paginationControl.currentPage}
                getPaginationNumbers={getPageNumbers}
                setCurrentPage={paginationControl.SetPage}
              />
            </div>
            <div>
              <ButtonIndicator operation = {'Next'}/>
            </div>
          </div>
          <div className={style.pageIndicator}>
            <div className={style.block}>
              <ItemsPerPageDropDown
                itemsPerPage={paginationControl.itemsPerPage}
                setItemsPerPage={paginationControl.SetPerPage}
              />
            </div>
            <div>&nbsp;</div>
            <div className={style.block}>
              <span className={style.text}> 
                Page 
              </span>
              <ChangePageInput
                currentPageValue={paginationControl.currentPage}
                setCurrentPage={paginationControl.SetPage}
                maximumPage={totalPages}
              />
              <span className={style.text}>
                of {totalPages} ({paginationControl.startIndex}-{paginationControl.endIndex} of {items.length} items)
              </span>
              <Icon className={style.icon} title={`Press Enter to navigate to the page. Your page must be between 1 and ${totalPages}`} icon={faInfoCircle} />
            </div>
          </div>
        </div>
      </>
  );
};

export default PaginatedList;